import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import useTableControls from '../../hooks/useTableControls';
import useAdminPageMeta from '../../hooks/useAdminPageMeta';
import { TableToolbar, TablePagination } from '../../components/admin/TableControls';

export default function PurchasesPage() {
  useAdminPageMeta('Purchases');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/admin/purchases')
      .then(({ data }) => setItems(data.purchases || data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Build unique status options from the data
  const statusOptions = useMemo(() => {
    const set = new Set();
    items.forEach((p) => { if (p.status) set.add(p.status); });
    return [{ value: 'all', label: 'All statuses' }, ...Array.from(set).map((s) => ({ value: s, label: s }))];
  }, [items]);

  const ctrl = useTableControls(items, {
    searchFn: (p, q) =>
      (p.shipping_name || '').toLowerCase().includes(q) ||
      (p.user_id || '').toLowerCase().includes(q) ||
      (p.purchase_id || '').toLowerCase().includes(q) ||
      (p.shipping_city || '').toLowerCase().includes(q) ||
      (p.shipping_country || '').toLowerCase().includes(q),
    filters: {
      status: (p, v) => (p.status || '') === v,
    },
    pageSize: 10,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10">
      <div className="mb-6 md:mb-8">
        <h1 className="hero-title text-3xl md:text-4xl text-white">Purchases</h1>
        <p className="text-ee-text2">Mocked orders — wire Stripe later.</p>
      </div>

      <TableToolbar
        query={ctrl.query}
        onQueryChange={ctrl.setQuery}
        placeholder="Search by buyer, order id, location…"
        filters={[
          {
            key: 'status',
            label: 'Status',
            value: ctrl.filterValues.status,
            onChange: (v) => ctrl.setFilter('status', v),
            options: statusOptions,
          },
        ]}
        pageSize={ctrl.pageSize}
        onPageSizeChange={ctrl.setPageSize}
      />

      <div className="ee-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-ee-bg2 text-ee-muted">
            <tr className="text-left">
              <th className="p-3">Order</th>
              <th className="p-3">Buyer</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Ship To</th>
              <th className="p-3">Status</th>
              <th className="p-3">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ee-border">
            {loading && <tr><td colSpan="6" className="p-6 text-center text-ee-muted">Loading…</td></tr>}
            {!loading && ctrl.filteredCount === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-ee-muted">
                {ctrl.total === 0 ? 'No purchases yet.' : 'No purchases match your filters.'}
              </td></tr>
            )}
            {ctrl.pageItems.map((p) => (
              <tr key={p.purchase_id} className="hover:bg-ee-card2">
                <td className="p-3 text-gold font-mono text-xs">{p.purchase_id?.slice(-8)}</td>
                <td className="p-3 text-white">{p.shipping_name || p.user_id}</td>
                <td className="p-3 text-white">${p.amount}</td>
                <td className="p-3 text-ee-text2 text-xs">{[p.shipping_city, p.shipping_state, p.shipping_country].filter(Boolean).join(', ')}</td>
                <td className="p-3">
                  <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-xs border border-green-500/30">{p.status}</span>
                </td>
                <td className="p-3 text-ee-dim text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <TablePagination
          page={ctrl.page}
          pageCount={ctrl.pageCount}
          total={ctrl.total}
          filteredCount={ctrl.filteredCount}
          onPageChange={ctrl.setPage}
        />
      </div>
    </div>
  );
}
